<template>
  <div>
    <StaticDataTable class="static-table-list">
      <template v-slot:header>
        <tr>
          <th width="25%" colspan="2">
            <Tooltip label="Stock thickness range this config / rule applies to. Minimum is inclusive, maximum is exclusive" helpStyle>Thickness Range</Tooltip>
          </th>

          <th>Glass Type</th>
          <th>
            <Tooltip label="Minimum height of a piece in the optimised sheet to be considered an offcut" helpStyle>Min. Offcut Height</Tooltip>
          </th>
          <th>
            <Tooltip label="Minimum width of a piece in the optimised sheet to be considered an offcut" helpStyle>Min. Offcut Width</Tooltip>            
          </th>
          <th>
            <Tooltip label="Trim automatically added to panels around straight line cuts" helpStyle>Panel Trim</Tooltip>  
          </th>
          <th>
            <Tooltip label="If enabled, panel edges can rest against the standard sheet edge" helpStyle>Panel On Sheet Edge</Tooltip>  
          </th>
          <th>
            <Tooltip label="If enabled, panel edges can rest against the sheet edge when using an offcut as the source sheet" helpStyle>Panel On Offcut Edge</Tooltip>  
          </th>
          <th>
            <Tooltip label="Trim automatically added to all edges of an offcut, when offcuts are used as the source sheet" helpStyle>Offcut Trim</Tooltip>  
          </th>
          <th>
            <Tooltip label="Trim automatically added to the individual edges for standard stock sheets" helpStyle>Trim Top</Tooltip>
          </th>
          <th>
            <Tooltip label="Trim automatically added to the individual edges for standard stock sheets" helpStyle>Trim Right</Tooltip>
          </th>
          <th>
            <Tooltip label="Trim automatically added to the individual edges for standard stock sheets" helpStyle>Trim Bottom</Tooltip>
          </th>
          <th>
            <Tooltip label="Trim automatically added to the individual edges for standard stock sheets" helpStyle>Trim Left</Tooltip>
          </th>

          <th width="5%" v-if="editing">Actions</th>          
        </tr>
      </template>
      <template v-slot:body>
        <tr v-for="config in configList" :key="config.id">
          <td v-if="editing">
            <FormInputText type="number" v-model="config.minThickness" @input="listUpdated" prepend="Min" addon="mm" />
          </td>
          <td v-if="editing">
            <FormInputText type="number" v-model="config.maxThickness" @input="listUpdated" prepend="Max" addon="mm" />
          </td>
          <td v-if="!editing" colspan="2">{{config.minThickness}}mm - {{config.maxThickness}}mm</td>

          <td>
            <FormSelect v-if="editing" v-model="config.glassType" :options="GlassTypeLookup" @input="listUpdated" />
            <span v-if="!editing">{{formatLookupName(config.glassType, GlassTypeLookup)}}</span>
          </td>
          <td>
            <FormInputText v-if="editing" v-model="config.glassMinOffcutHeight" @input="listUpdated" />
            <span v-if="!editing">{{config.glassMinOffcutHeight}}</span>
          </td>
          <td>
            <FormInputText v-if="editing" v-model="config.glassMinOffcutWidth" @input="listUpdated" />
            <span v-if="!editing">{{config.glassMinOffcutWidth}}</span>
          </td>
          <td>
            <FormInputText v-if="editing" v-model="config.panelTrim" @input="listUpdated" />
            <span v-if="!editing">{{config.panelTrim}}</span>
          </td>
          <td>
            <FormCheckbox v-if="editing" v-model="config.panelEdgeOnSheetEdgeEnabled" @change="listUpdated" />
            <IconBoolean v-if="!editing" :value="config.panelEdgeOnSheetEdgeEnabled"></IconBoolean>
          </td>
          <td>
            <FormCheckbox v-if="editing" v-model="config.panelEdgeOnOffcutEdgeEnabled" @change="listUpdated" />
            <IconBoolean v-if="!editing" :value="config.panelEdgeOnOffcutEdgeEnabled"></IconBoolean>
          </td>
          <td>
            <FormInputText v-if="editing" v-model="config.offcutEdgeTrim" @input="listUpdated" />
            <span v-if="!editing">{{config.offcutEdgeTrim}}</span>
          </td>
          <td>
            <FormInputText v-if="editing" v-model="config.glassTrimTop" @input="listUpdated" />
            <span v-if="!editing">{{config.glassTrimTop}}</span>
          </td>
          <td>
            <FormInputText v-if="editing" v-model="config.glassTrimRight" @input="listUpdated" />
            <span v-if="!editing">{{config.glassTrimRight}}</span>
          </td>     
          <td>
            <FormInputText v-if="editing" v-model="config.glassTrimBottom" @input="listUpdated" />
            <span v-if="!editing">{{config.glassTrimBottom}}</span>
          </td>
          <td>
            <FormInputText v-if="editing" v-model="config.glassTrimLeft" @input="listUpdated" />
            <span v-if="!editing">{{config.glassTrimLeft}}</span>
          </td>

          <td v-if="editing">
            <ActionDropMenu :tableAction="true">
              <DropdownMenuItem @click="deleteConfig(config.id)" icon="fa-trash-alt">Delete</DropdownMenuItem>
            </ActionDropMenu>
          </td>
        </tr>
      </template>
      <template v-slot:empty v-if="configList.length == 0">
        No config items found
      </template>
    </StaticDataTable>
    <button v-if="editing" class="btn btn-white btn-xs" @click.prevent="addConfig"><i class="far fa-plus"></i> Add Config</button>
  </div>
</template>


<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import formMixin from '@/mixins/form'

import { GlassTypes, GlassTypeLookup } from '@/store/types'

export default {
  name: 'OptimiserStockConfigEditor',
  model: {
    event: 'update'
  },
  mixins: [formMixin],
  props: {
    editing: Boolean,
    value: Array
  },
  data() {
    return {
      configList: []
    }
  },
  computed: {
  },
  watch: {
    value: function() {
      this.$data.configList = _.cloneDeep(this.$props.value)
    }
  },
  created() {
    this.GlassTypeLookup = GlassTypeLookup
  },
  mounted() {
  },
  methods: {
    addConfig() {
      this.configList.push({
        id: `temp-id-` + uuidv4(),
        isNew: true,
        minThickness: 0,
        maxThickness: 0,
        glassType: GlassTypes.all,
        glassMinOffcutHeight: 0,
        glassMinOffcutWidth: 0,
        panelTrim: 0,
        panelEdgeOnSheetEdgeEnabled: false,
        panelEdgeOnOffcutEdgeEnabled: true,
        offcutEdgeTrim: 0,
        glassTrimTop: 0,
        glassTrimRight: 0,
        glassTrimBottom: 0,
        glassTrimLeft: 0,
      })
      this.listUpdated()
    },
    deleteConfig(id) {
      this.configList = this.configList.filter(config => config.id != id)
      this.listUpdated()
    },
    listUpdated() {
      this.$emit('update', this.configList)
    }
  }


}
</script>